import { Formik } from 'formik';
import * as yup from 'yup';
import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { saveAs } from 'file-saver';
import FileInputComponent from '../../../../components/common/forms/FileInput/FileInputComponent';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { UPLOAD_MAX_SIZE_IMAGE, FILE_UPLOAD_TYPES, API_URL } from '../../../../utils/constants';
import FilesApi from '../../../../services/api/FilesApi';
import productImportsApi from '../../../../services/api/ProductImportsApi';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import ImportResults from '~/src/scenes/imports/components/ResultDtoDisplay';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import exportIcon from '~/src/static/img/export.svg';
import styled from 'styled-components';
import { Colors } from '~/src/scss/common/style';
import DataOperationsNav from '~/src/components/common/navigation/DataOperationsNav';
import { ExportForm } from '~/src/components/common/forms/ExportForm/ExportForm';
import { GenerateExportOfferRequestDto } from '~/src/types/GenerateExportOfferRequestDto';

const validationSchema = yup.object().shape({
  certificate: yup.mixed().test('fileSize', 'Моля използвайте файлове до 3MB', (value) => {
    if (typeof value === 'undefined' || value.length <= 0) {
      return true;
    }
    return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
  }),
});

export default function UploadForImport(): JSX.Element {
  const [result, setResult] = React.useState(null);
  const [input, setInput] = React.useState('');

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;
    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_IMPORTS };
      var fileUploadResp = await FilesApi.create(fileData);

      if (fileUploadResp.status === StatusCodes.OK) {
        var result = await productImportsApi.importAnUpload(fileUploadResp.data[0].id);
        setResult(result.data);
      }
    }
    setSubmitting(false);
  };

  const ExportForms = () => {
    const [logisticsImport, setLogisticsImport] = React.useState('');
    const [offerInput, setOfferInput] = React.useState('');

    const onExportLogistics = async () => {
      const exportLogisticsResult = await productImportsApi.exportLogistics(logisticsImport);
      const courseSheet = exportLogisticsResult.data;

      if (courseSheet) {
        const blob = new Blob([courseSheet], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `export-logistics.xlsx`);
      }
    };

    const onExportOffer = async (selectedMarginsId: string) => {
      let generateOffferDto: GenerateExportOfferRequestDto = {
        cuGtins: offerInput.split(',').map((x) => x.trim()),
        marginsId: selectedMarginsId,
      }

      const exportOfferResult = await productImportsApi.exportOffer(generateOffferDto);
      const courseSheet = exportOfferResult.data;

      if (courseSheet) {
        const blob = new Blob([courseSheet], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `export-offer.xlsx`);
      }
    };

    return (
      <div>
        <WhitePad>
          <div style={{ display: 'flex' }}>
            <DataOperationsNav />
            <div>
              <TitleComponent Heading="h2" btnSizeLeft="sm" title="Logistics" />

              <ExportForm
                title="Export"
                description="Leave input empty to export full db. List Cu Gtins separated by comma, to export only those products."
                input={logisticsImport}
                setInput={setLogisticsImport}
                onExport={onExportLogistics}
                buttonTitle="Export Logistics"
                buttonIcon={exportIcon}
              />

              <ExportForm
                title="Offer generator"
                description="Leave input empty to export full db. List Cu Gtins separated by comma, to export only those products."
                input={offerInput}
                setInput={setOfferInput}
                onExport={onExportOffer}
                buttonTitle="Export Offer"
                buttonIcon={exportIcon}
                isSelectableMargins={true}
              />
            </div>
          </div>
        </WhitePad>
      </div>
    );
  };

  return (
    <div>
      <WhitePad>
        <div style={{ display: 'flex' }}>
          <div>
            <ExportForms />
            <FormWrapper>
              <div>
                <Title>Import Logistics</Title>
                <StyleP>The excel needs to have the following columns in that specific order:</StyleP>
                <Table>
                  <tbody>
                    <tr>
                      <StyleTd>
                        [A] <b>CuGtin</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [B] <b>DuGtin</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [C] <b>DpGtin</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [D] <b>Name</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [E] <b>CuPerDisplay</b> (0-9)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [F] <b>CuPerDu</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [G] <b>DisplayPerDu</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [H] <b>DuPerLayer</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [I] <b>DuPerPallet</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [J] <b>LayerPerPallet</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [K] <b>Pallet</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [L] <b>Manufacturer</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [M] <b>Suppliers</b>
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [N] <b>Manufacturer SKU</b> (string)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [O] <b>Brand</b> (string)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [P] <b>Category</b> (string)
                      </StyleTd>
                      <StyleTd>zzz.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [Q] <b>HSCode</b> (string)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [Q] <b>DeletePreviousPackagingInfo</b> (yes/no)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [Q] <b>DeleteAlsoSizingInfo</b> (yes/no)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [Q] <b>CreateIfMissing</b> (yes/no)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [Q] <b>UpdateName</b> (yes/no)
                      </StyleTd>
                      <StyleTd>zzz</StyleTd>
                    </tr>
                  </tbody>
                </Table>
                <Formik
                  initialValues={{
                    xlsx: [],
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
                >
                  {({ values, handleChange, handleBlur, isSubmitting, setFieldValue, onSubmit, handleSubmit }) => (
                    <form autoComplete="off" onSubmit={handleSubmit} style={{ marginTop: '30px' }}>
                      <FormRow>
                        <FileInputComponent
                          name="xlsx"
                          value={values.xlsx}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          cols={12}
                        />
                      </FormRow>
                      <div style={{ marginTop: '40px' }}>
                        <PrimaryButton
                          variant="primary"
                          type="submit"
                          title="Upload"
                          isSubmitting={isSubmitting}
                        />
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </FormWrapper>
            <ImportResults result={result} />
          </div>
        </div>
      </WhitePad>
    </div>
  );
}

const Table = styled.div`
  border: 1px solid #fff;
  border-radius: 10px;
  overflow: hidden;
`;
const StyleTd = styled.td`
  width: 300px;
  border: 1px solid #fff;
  padding: 7px;
`;

const StyleP = styled.p`
  font-size: 14px;
  line-height: 17pxl
  color: #343434;
  text-align: center;
  margin-bottom: 43px;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  color: ${Colors.darkBrown};
`;